import {
    consentKeys,
    fetchConsentViewsByDiaryId,
    resolveConsentReadLocale,
} from "@/features/consent/api/selectConsentById";
import { getConsentRedirectTarget } from "@/features/consent/utils/consentGating";
import { diaryByIdQueryOptions } from "@/features/diaries/api";
import {
    getPatientFirstSetupStepPath,
    type PatientSetupStepPath,
} from "@/features/diaries/utils/missingDetailsBuilders";
import type { Auth } from "@/utils/auth";
import type { StatefulDiaryT } from "@patient-watch/types";
import type { QueryClient } from "@tanstack/react-query";
import { redirect } from "@tanstack/react-router";

interface DiaryReadyLoaderContext {
    queryClient: QueryClient;
    auth: Auth;
}

const SETUP_STEP_TO_ROUTE: Record<PatientSetupStepPath, `/diaries/$diaryId/${PatientSetupStepPath}`> = {
    "setup/hospital-number": "/diaries/$diaryId/setup/hospital-number",
    "setup/reference-date": "/diaries/$diaryId/setup/reference-date",
    "setup/side": "/diaries/$diaryId/setup/side",
};

export async function ensureDiaryReadyLoader(
    context: DiaryReadyLoaderContext,
    diaryId: string
): Promise<{ diary: StatefulDiaryT | null }> {
    const diary = await context.queryClient.ensureQueryData(diaryByIdQueryOptions(diaryId));
    const isPatientViewer = (context.auth?.roles?.isPatientOnly ?? false) || context.auth?.id === diary?.patient_id;

    if (isPatientViewer && diary) {
        const locale = resolveConsentReadLocale();
        const consents = await context.queryClient.ensureQueryData({
            queryKey: consentKeys.byDiaryId(diaryId, locale),
            queryFn: () => fetchConsentViewsByDiaryId(diaryId, locale),
        });
        const consentTarget = getConsentRedirectTarget(consents, context.auth?.id);
        if (consentTarget) {
            throw redirect(consentTarget);
        }

        const setupStep = getPatientFirstSetupStepPath(diary);
        if (setupStep) {
            throw redirect({
                to: SETUP_STEP_TO_ROUTE[setupStep],
                params: { diaryId },
            });
        }
    }

    return { diary: diary ?? null };
}
